/**
 * VÉRAULT — Owner Access Hub (/owner-access)
 *
 * Private command door. Not linked from public nav. Accessible by
 * typing /owner-access in any browser. Surfaces:
 *
 *   · Public Site
 *   · Advisor Studio
 *   · Client Suite
 *   · Jackson Trip Portal
 *   · Login
 *   · Test Credentials (reveals on click; advisor/owner only via API)
 *   · Latest Build Info (live from /api/owner-access/version)
 *
 * Style: editorial luxury — same VÉRAULT vocabulary as the portal
 * (serif headers, gold rules, calm tiles). Mobile-first.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight, Lock, Globe, Compass, Briefcase, Key, Copy, Check, Sparkles, RefreshCw, LogOut, Anchor,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const UNLOCK_KEY = "verault.ownerhub.unlock";
const UNLOCK_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readUnlock() {
  try {
    const raw = localStorage.getItem(UNLOCK_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.token || !obj?.exp) return null;
    if (Date.now() > obj.exp) { localStorage.removeItem(UNLOCK_KEY); return null; }
    return obj;
  } catch { return null; }
}
function writeUnlock(token) {
  try {
    localStorage.setItem(UNLOCK_KEY, JSON.stringify({ token, exp: Date.now() + UNLOCK_TTL_MS }));
  } catch { /* ignore */ }
}
function clearUnlock() {
  try { localStorage.removeItem(UNLOCK_KEY); } catch { /* ignore */ }
}

// ───────────────────────────────────────────────────────────────────
// PIN GATE — 6 calm boxes. No "forgot" link (intentional).
// ───────────────────────────────────────────────────────────────────
function PinGate({ onUnlocked }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const refs = useRef([]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  const setAt = (i, v) => {
    const cleaned = (v || "").replace(/\D/g, "").slice(0, 1);
    setDigits((p) => { const n = [...p]; n[i] = cleaned; return n; });
    if (cleaned && i < 5) refs.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (txt.length === 0) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) next[i] = txt[i];
    setDigits(next);
    refs.current[Math.min(txt.length, 5)]?.focus();
    if (txt.length === 6) submit(txt);
  };
  const submit = async (override) => {
    const pin = override || digits.join("");
    if (pin.length !== 6) { setError("Enter all 6 digits."); return; }
    setSubmitting(true); setError(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/owner-access/check-pin`, { pin });
      writeUnlock(r.data.token);
      onUnlocked();
    } catch (e) {
      setError(e?.response?.data?.detail || "Incorrect PIN.");
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-5" data-testid="pin-gate">
      <div className="max-w-sm w-full">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lock className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--foreground-muted))]">Private door</p>
        </div>
        <h1 className="font-serif text-[28px] tracking-tight text-center leading-tight">Owner Access</h1>
        <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] text-center">
          Enter your six-digit access code.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              value={d}
              onChange={(e) => setAt(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              className="size-11 sm:size-12 text-center font-mono text-[18px] bg-white border border-[rgba(0,0,0,0.15)] rounded outline-none focus:border-[hsl(var(--accent-gold))]"
              data-testid={`pin-digit-${i}`}
            />
          ))}
        </div>
        {error && <p className="mt-3 font-mono text-[10.5px] text-[#8B4513] text-center" data-testid="pin-error">{error}</p>}
        <button
          onClick={() => submit()}
          disabled={submitting || digits.join("").length !== 6}
          className="mt-5 w-full py-2.5 rounded bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[hsl(var(--accent-gold))] disabled:opacity-40 transition-colors"
          data-testid="pin-submit"
        >
          {submitting ? "Verifying…" : "Enter"}
        </button>
      </div>
    </div>
  );
}

function Tile({ to, eyebrow, title, hint, icon, external, testId }) {
  const inner = (
    <div className="group h-full p-5 rounded-md border border-[rgba(0,0,0,0.06)] bg-white/70 hover:border-[hsl(var(--accent-gold))]/50 hover:bg-white transition-colors">
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-sm bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">{eyebrow}</p>
          <h3 className="mt-1 font-serif text-[19px] tracking-tight leading-tight">{title}</h3>
          {hint && <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">{hint}</p>}
        </div>
        <ArrowUpRight className="size-3.5 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] flex-shrink-0" strokeWidth={1.5} />
      </div>
    </div>
  );
  if (external) {
    return <a href={to} target="_blank" rel="noreferrer" className="block h-full" data-testid={testId}>{inner}</a>;
  }
  return <Link to={to} className="block h-full" data-testid={testId}>{inner}</Link>;
}

function CredField({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }
    catch { /* ignore */ }
  };
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] w-[80px]">{label}</span>
      <code className="flex-1 px-2 py-1 bg-[hsl(var(--background-paper))] rounded text-[12px] font-mono select-all">{value}</code>
      <button onClick={copy} className="size-7 rounded inline-flex items-center justify-center hover:bg-[hsl(var(--background-paper))]" data-testid={`copy-${label.toLowerCase()}`}>
        {copied ? <Check className="size-3 text-[hsl(168,22%,32%)]" /> : <Copy className="size-3 text-[hsl(var(--foreground-muted))]" />}
      </button>
    </div>
  );
}

function CredentialsTile() {
  const [creds, setCreds] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const reveal = async () => {
    setLoading(true); setError(null);
    try {
      // Try with credentials (cookie session) — works after login.
      const r = await axios.get(`${BACKEND_URL}/api/owner-access/credentials`, { withCredentials: true });
      setCreds(r.data);
    } catch (e) {
      setError(e?.response?.status === 401 || e?.response?.status === 403
        ? "Sign in as the owner first → then return here and reveal."
        : (e?.response?.data?.detail || "Couldn't fetch credentials."));
    } finally { setLoading(false); }
  };
  return (
    <div className="p-5 rounded-md border border-[rgba(0,0,0,0.06)] bg-white/70" data-testid="hub-tile-credentials">
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-sm bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] flex items-center justify-center flex-shrink-0">
          <Key className="size-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Verification</p>
          <h3 className="mt-1 font-serif text-[19px] tracking-tight">Test credentials</h3>
          {!creds && (
            <>
              <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">Owner / Jackson Suite. Click to reveal.</p>
              <button onClick={reveal} disabled={loading} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[hsl(var(--accent-gold))]/40 text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))]/8 font-mono text-[10px] uppercase tracking-[0.16em]" data-testid="reveal-credentials">
                {loading ? "Loading…" : "Reveal"}
              </button>
              {error && <p className="mt-2 font-mono text-[10.5px] text-[#8B4513]">{error}</p>}
            </>
          )}
          {creds && (
            <div className="mt-3 space-y-3" data-testid="credentials-revealed">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))]">Owner / Admin</p>
                <CredField label="Email" value={creds.owner?.email} />
                <CredField label="Password" value={creds.owner?.password} />
                <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))] mt-1">{creds.owner?.use_for}</p>
              </div>
              <div className="pt-2 border-t border-[rgba(0,0,0,0.06)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))]">Jackson Suite</p>
                <CredField label="Jonathan" value={creds.jackson?.jonathan_email} />
                <CredField label="Nadranne" value={creds.jackson?.nadranne_email} />
                <CredField label="Password" value={creds.jackson?.shared_password} />
                <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))] mt-1">Door: <code>{creds.jackson?.door}</code></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// LATEST SHIP TILE — answers "where do I look to confirm latest ship?"
// ───────────────────────────────────────────────────────────────────
function LatestShipTile() {
  const [ship, setShip] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/owner-access/latest-ship`, { params: { _t: Date.now() } })
      .then((r) => setShip(r.data))
      .catch(() => setShip({ available: false }));
  }, []);
  if (!ship || !ship.available) return null;
  return (
    <div className="sm:col-span-2 p-5 rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/5" data-testid="hub-tile-latest-ship">
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-sm bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] flex items-center justify-center flex-shrink-0">
          <Anchor className="size-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Latest ship · Verify here</p>
            {ship.date && <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">{ship.date}</span>}
            {ship.wave && <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">· {ship.wave}</span>}
          </div>
          <h3 className="mt-1 font-serif text-[21px] tracking-tight leading-tight">{ship.title}</h3>
          {ship.summary && <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">{ship.summary}</p>}
          {ship.verify_url && (
            <Link to={ship.verify_url} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[hsl(var(--accent-gold))]" data-testid="latest-ship-verify">
              Open {ship.verify_url} <ArrowUpRight className="size-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function BuildInfoTile() {
  const [info, setInfo] = useState(null);
  const refresh = React.useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/owner-access/version`, { params: { _t: Date.now() } });
      setInfo(r.data);
    } catch {
      try {
        const r = await axios.get(`/version.json?_t=${Date.now()}`);
        setInfo(r.data);
      } catch { setInfo({ build_human: "unavailable" }); }
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <div className="p-5 rounded-md border border-[rgba(0,0,0,0.06)] bg-white/70" data-testid="hub-tile-build">
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-sm bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] flex items-center justify-center flex-shrink-0">
          <Sparkles className="size-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Verification</p>
            <button onClick={refresh} className="size-7 rounded inline-flex items-center justify-center hover:bg-[hsl(var(--background-paper))]" data-testid="refresh-version" title="Refresh">
              <RefreshCw className="size-3 text-[hsl(var(--foreground-muted))]" />
            </button>
          </div>
          <h3 className="mt-1 font-serif text-[19px] tracking-tight">Latest build</h3>
          {info ? (
            <div className="mt-2 space-y-1 font-mono text-[11px]">
              <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[80px]">Built</span><span data-testid="build-timestamp">{info.build_human || info.build_timestamp || "—"}</span></div>
              <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[80px]">Commit</span><span data-testid="build-commit">{info.git_commit || "n/a"}</span></div>
              <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[80px]">Branch</span><span>{info.git_branch || "n/a"}</span></div>
              <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[80px]">Environment</span><span className="text-[hsl(var(--accent-gold))]" data-testid="build-environment">{info.environment || "unknown"}</span></div>
            </div>
          ) : (
            <p className="mt-2 font-mono text-[11px] text-[hsl(var(--foreground-muted))]">Reading…</p>
          )}
        </div>
      </div>
    </div>
  );
}

function OwnerAccessHubBody() {
  // SW poison-pill: force-unregister any service worker that might
  // be holding stale code, then hard-purge caches. Idempotent — does
  // nothing on first visit if no SW exists.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister().catch(() => {}));
      });
    }
    if ("caches" in window) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k).catch(() => {})));
    }
  }, []);

  const lock = () => { clearUnlock(); window.location.reload(); };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="owner-access-hub">
      <div className="max-w-5xl mx-auto px-5 py-12 sm:py-16">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Lock className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--foreground-muted))]">Private · Not indexed</p>
          </div>
          <button onClick={lock} className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-[rgba(0,0,0,0.08)] hover:border-[hsl(var(--accent-gold))]/40 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]" data-testid="hub-lock">
            <LogOut className="size-3" /> Lock
          </button>
        </div>
        <h1 className="font-serif text-[34px] sm:text-[44px] tracking-tight leading-tight">Owner Access</h1>
        <p className="mt-2 font-accent italic text-[15px] text-[hsl(var(--foreground-muted))] max-w-xl">
          A calm command door into every surface of the suite. Verify the latest ship, jump into any portal, and confirm what&apos;s actually live.
        </p>

        <div className="mt-8 h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))]/30 to-transparent" />

        <section className="mt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">Where to verify</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LatestShipTile />
          </div>
        </section>

        <section className="mt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">Surfaces</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Tile to="/" eyebrow="Public" title="Public Site" hint="Marketing home — what guests see." icon={<Globe className="size-4" strokeWidth={1.5} />} testId="hub-tile-public" />
            <Tile to="/portal/advisor" eyebrow="Studio" title="Advisor Studio" hint="Trip ledger, itinerary, logistics, content." icon={<Briefcase className="size-4" strokeWidth={1.5} />} testId="hub-tile-advisor" />
            <Tile to="/portal/client" eyebrow="Suite" title="Client Suite" hint="Member view — calm experience reading room." icon={<Compass className="size-4" strokeWidth={1.5} />} testId="hub-tile-client" />
            <Tile to="/suite/jackson" eyebrow="Family" title="Jackson Trip Portal" hint="Branded landing door for the Jackson family." icon={<Sparkles className="size-4" strokeWidth={1.5} />} testId="hub-tile-jackson" />
            <Tile to="/owner-access" eyebrow="Auth" title="Studio sign-in" hint="The advisor door (email + password)." icon={<Key className="size-4" strokeWidth={1.5} />} testId="hub-tile-login" />
            <Tile to="/portal/advisor/journeys" eyebrow="Studio" title="Journeys list" hint="Every active trip in the studio." icon={<Compass className="size-4" strokeWidth={1.5} />} testId="hub-tile-journeys" />
          </div>
        </section>

        <section className="mt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">Verification</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BuildInfoTile />
            <CredentialsTile />
          </div>
        </section>

        <p className="mt-10 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] text-center">
          Direct routes: <code>/owner-access</code> · <code>/login</code> · <code>/portal/advisor</code> · <code>/portal/client</code> · <code>/suite/jackson</code> · <code>/</code>
        </p>
      </div>
    </div>
  );
}

export default function OwnerAccessHub() {
  const [unlocked, setUnlocked] = useState(() => Boolean(readUnlock()));
  if (!unlocked) return <PinGate onUnlocked={() => setUnlocked(true)} />;
  return <OwnerAccessHubBody />;
}
